import React, { useMemo } from 'react';
import useGameStore from '../../store/gameStore';
import { useTranslation } from '../../i18n';
import { computeRseScore, TIER_COLORS } from '../../store/helpers/rseScore';
import { getCompanyHseCompliance, HSE_TRAININGS } from '../../store/helpers/hseCompliance';
import { ShieldCheck, Leaf, Users, Scale, Award, Lock, GraduationCap, AlertTriangle, Stethoscope, FlaskConical, HardHat } from 'lucide-react';

/**
 * RSEDashboard — consolidated RSE / HSE indicator panel.
 *
 * Locked behind hiring Claire Maréchal (`role-claire-marechal` unlock
 * `rse_dashboard`). Without her, the panel shows a lock state explaining
 * what hiring her unlocks. Once unlocked, displays :
 *   - Overall score (0-100) with EcoVadis-style tier badge
 *   - 4 sub-scores : CO2/km, green fleet %, parity, wage spread
 *   - HSE Compliance card with per-training breakdown (FCO, médical, ADR, CACES)
 *
 * 01/06/2026 — QHSE / RSE module (Phase 1-5).
 */
export default function RSEDashboard() {
  const state = useGameStore();
  const { t, lang } = useTranslation();
  // Subscribe to ONLY the slices computeRseScore reads — keeps the
  // useMemo deps array honest for react-hooks/exhaustive-deps.
  const vehicles = useGameStore(s => s.vehicles);
  const drivers = useGameStore(s => s.drivers);
  const totalDays = useGameStore(s => s.totalDays) || 0;
  const hasUnlock = typeof state.hasSupportUnlock === 'function'
    && state.hasSupportUnlock('rse_dashboard');

  const score = useMemo(() => {
    if (!hasUnlock) return null;
    return computeRseScore({ vehicles, drivers });
  }, [hasUnlock, vehicles, drivers]);

  const hseCompliance = useMemo(() => {
    if (!hasUnlock) return null;
    return getCompanyHseCompliance(drivers || [], totalDays);
  }, [hasUnlock, drivers, totalDays]);

  // Localised tier label (the rseScore TIER_COLORS shipped FR labels).
  const tierLabelKey = score ? `rse_tier_${score.tier}` : null;

  if (!hasUnlock) {
    return (
      <div className="bg-ink-800 rounded-2xl p-6 border border-white/5" data-testid="rse-dashboard-locked">
        <div className="flex items-center gap-3 mb-4">
          <div className="p-2 rounded-lg bg-slate-700/40">
            <Lock className="w-5 h-5 text-slate-400" />
          </div>
          <div>
            <h3 className="text-base font-display font-bold text-slate-200">
              {t('rse_dashboard_title')}
            </h3>
            <p className="text-xs text-slate-500">{t('rse_locked_subtitle')}</p>
          </div>
        </div>
        <p className="text-sm text-slate-400 leading-relaxed">
          {t('rse_locked_recruit_prefix')}{' '}
          <strong className="text-emerald-300">Claire Maréchal{/* i18n-keep — person name */}</strong>{' '}
          {t('rse_locked_recruit_suffix')}
        </p>
        <ul className="mt-3 text-xs text-slate-400 space-y-1.5 list-none pl-0">
          <li className="flex items-start gap-2"><span className="text-emerald-400 mt-0.5">▸</span> {t('rse_locked_bullet_score')}</li>
          <li className="flex items-start gap-2"><span className="text-emerald-400 mt-0.5">▸</span> {t('rse_locked_bullet_audit')}</li>
          <li className="flex items-start gap-2"><span className="text-emerald-400 mt-0.5">▸</span> {t('rse_locked_bullet_contracts')}</li>
          <li className="flex items-start gap-2"><span className="text-emerald-400 mt-0.5">▸</span> {t('rse_locked_bullet_training')}</li>
        </ul>
      </div>
    );
  }

  const tone = TIER_COLORS[score.tier] || TIER_COLORS.none;
  const tierLabel = tierLabelKey ? t(tierLabelKey) : tone.label;

  return (
    <div className="bg-ink-800 rounded-2xl p-6 border border-white/5 space-y-5" data-testid="rse-dashboard">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="p-2 rounded-lg bg-emerald-500/15">
            <ShieldCheck className="w-5 h-5 text-emerald-300" />
          </div>
          <div>
            <h3 className="text-base font-display font-bold text-slate-200">
              {t('rse_dashboard_title')}
            </h3>
            <p className="text-xs text-slate-500">{t('rse_pilot_by_claire')}</p>
          </div>
        </div>
        <div className={`px-3 py-1.5 rounded-full text-xs font-mono font-bold ${tone.bg} ${tone.text} border ${tone.border}`}
             data-testid="rse-tier-badge">
          <Award className="w-3.5 h-3.5 inline mr-1" />
          {tierLabel}
        </div>
      </div>

      {/* Overall score — big readout */}
      <div className="text-center bg-gradient-to-br from-slate-900/40 to-ink-700/40 rounded-xl p-4 border border-white/5">
        <p className="text-[10px] uppercase tracking-widest text-slate-500 font-mono mb-1">{t('rse_overall_label')}</p>
        <p className={`text-5xl font-display font-black ${tone.text}`} data-testid="rse-overall-score">
          {score.overall}
          <span className="text-xl text-slate-500 font-mono ml-1">/100</span>
        </p>
        <p className="text-xs text-slate-400 mt-2">
          {t('rse_realtime_caption')}
        </p>
      </div>

      {/* 4 sub-scores */}
      <div className="grid grid-cols-2 gap-3" data-testid="rse-sub-scores">
        <SubScoreCard
          icon={Leaf}
          label={t('rse_sub_co2')}
          value={score.sub.co2}
          caption={`${score.raw.vehiclesTotal} ${pluralVehicle(score.raw.vehiclesTotal, lang, t)} ${t('rse_caption_in_fleet')}`}
          weight={score.weights.co2}
          testId="rse-sub-co2"
          weightLabel={t('rse_weight_label')}
        />
        <SubScoreCard
          icon={Leaf}
          label={t('rse_sub_green')}
          value={score.sub.green}
          caption={`${score.raw.greenVehiclesCount} ${pluralVehicle(score.raw.greenVehiclesCount, lang, t)} ${t('rse_caption_low_carbon')}`}
          weight={score.weights.green}
          testId="rse-sub-green"
          weightLabel={t('rse_weight_label')}
        />
        <SubScoreCard
          icon={Users}
          label={t('rse_sub_parity')}
          value={score.sub.parity}
          caption={`${score.raw.womenDrivers} / ${score.raw.driversTotal} ${t('rse_caption_drivers')}`}
          weight={score.weights.parity}
          testId="rse-sub-parity"
          weightLabel={t('rse_weight_label')}
        />
        <SubScoreCard
          icon={Scale}
          label={t('rse_sub_wage')}
          value={score.sub.wage}
          caption={t('rse_caption_wage_spread')}
          weight={score.weights.wage}
          testId="rse-sub-wage"
          weightLabel={t('rse_weight_label')}
        />
      </div>

      <p className="text-[10px] text-slate-500 italic leading-relaxed border-t border-white/5 pt-3">
        <strong className="text-slate-400">{t('rse_methodology_label')}</strong> {t('rse_methodology_text')}
      </p>

      {/* HSE Compliance — Phase 4 (FCO) + Phase 5 (full matrix) */}
      <HseComplianceCard compliance={hseCompliance} t={t} />
    </div>
  );
}

function pluralVehicle(n, _lang, t) {
  return n > 1 ? t('rse_caption_vehicles_plural') : t('rse_caption_vehicles_singular');
}

const TRAINING_ICONS = {
  fco: GraduationCap,
  medical: Stethoscope,
  adr: FlaskConical,
  caces: HardHat,
};

function HseComplianceCard({ compliance, t }) {
  if (!compliance) return null;
  const { overall, counts, details } = compliance;
  const color =
    overall >= 90 ? 'text-emerald-300'
    : overall >= 70 ? 'text-sky-300'
    : overall >= 50 ? 'text-amber-300'
    : 'text-danger';

  // Per-training aggregate: count how many drivers in each status per
  // training key (applicable only).
  const perTraining = {};
  for (const key of Object.keys(HSE_TRAININGS)) {
    perTraining[key] = { compliant: 0, expiring: 0, expired: 0, applicable: 0 };
  }
  for (const d of details) {
    for (const [key, t2] of Object.entries(d.trainings)) {
      if (t2.applicable === false) continue;
      perTraining[key].applicable += 1;
      if (t2.status === 'compliant') perTraining[key].compliant += 1;
      else if (t2.status === 'expiring') perTraining[key].expiring += 1;
      else if (t2.status === 'expired') perTraining[key].expired += 1;
    }
  }

  // Per-driver attention list: pick the driver's WORST training entry
  // when it's expiring/expired, sorted by urgency.
  const attention = [];
  for (const d of details) {
    let worst = null;
    for (const [key, t2] of Object.entries(d.trainings)) {
      if (t2.applicable === false || t2.status === 'compliant' || t2.status === 'unknown') continue;
      if (!worst || (t2.daysRemaining ?? Infinity) < (worst.daysRemaining ?? Infinity)) {
        worst = { trainingKey: key, ...t2 };
      }
    }
    if (worst) {
      attention.push({
        driverId: d.driverId,
        name: d.name,
        ...worst,
      });
    }
  }
  attention.sort((a, b) => (a.daysRemaining ?? 0) - (b.daysRemaining ?? 0));

  return (
    <div className="bg-ink-700/30 rounded-xl p-4 border border-white/5 mt-3" data-testid="hse-compliance-card">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <GraduationCap className="w-4 h-4 text-emerald-300" />
          <span className="text-xs uppercase tracking-widest text-slate-300 font-mono font-bold">
            {t('hse_compliance_title')}
          </span>
        </div>
        <span className={`text-2xl font-display font-bold ${color}`} data-testid="hse-compliance-score">
          {overall}<span className="text-sm text-slate-500 font-mono ml-1">/100</span>
        </span>
      </div>
      <div className="grid grid-cols-3 gap-2 mb-3">
        <CountChip label={t('hse_chip_compliant')} value={counts.compliant} tone="emerald" />
        <CountChip label={t('hse_chip_expiring')} value={counts.expiring} tone="amber" />
        <CountChip label={t('hse_chip_expired')} value={counts.expired} tone="danger" />
      </div>

      {/* Per-training breakdown */}
      <div className="grid grid-cols-2 gap-2 mb-3" data-testid="hse-trainings-grid">
        {Object.entries(perTraining).map(([key, agg]) => (
          <TrainingRow key={key} trainingKey={key} agg={agg} t={t} />
        ))}
      </div>

      {attention.length > 0 && (
        <div className="space-y-1.5" data-testid="hse-attention-list">
          {attention.slice(0, 5).map(s => (
            <div key={`${s.driverId}-${s.trainingKey}`}
                 className="flex items-center justify-between text-[11px] bg-ink-800/60 rounded px-2 py-1.5"
                 data-testid={`hse-driver-${s.driverId}`}>
              <span className="flex items-center gap-1.5 text-slate-300">
                <AlertTriangle className={`w-3 h-3 ${s.status === 'expired' ? 'text-danger' : 'text-amber-300'}`} />
                {s.name}
                <span className="text-[9px] text-slate-500 uppercase tracking-wider ml-1">
                  {t(`hse_training_${s.trainingKey}_short`)}
                </span>
              </span>
              <span className={`font-mono ${s.status === 'expired' ? 'text-danger' : 'text-amber-300'}`}>
                {s.status === 'expired'
                  ? `${t('hse_expired_at')} J${s.expiresOn}`
                  : `${s.daysRemaining} ${t('hse_days_remaining')}`}
              </span>
            </div>
          ))}
        </div>
      )}
      {attention.length === 0 && (
        <p className="text-[11px] text-slate-500 italic">
          {t('hse_no_attention')}
        </p>
      )}
      <p className="text-[10px] text-slate-500 italic mt-3 leading-relaxed">
        {t('hse_footer_methodology')}
      </p>
    </div>
  );
}

function TrainingRow({ trainingKey, agg, t }) {
  const Icon = TRAINING_ICONS[trainingKey] || GraduationCap;
  const total = agg.applicable;
  const ratio = total === 0 ? 1 : agg.compliant / total;
  const color =
    ratio >= 0.9 ? 'text-emerald-300'
    : ratio >= 0.6 ? 'text-sky-300'
    : ratio >= 0.3 ? 'text-amber-300'
    : 'text-danger';
  return (
    <div className="bg-ink-800/50 rounded px-2 py-1.5 border border-white/5"
         data-testid={`hse-training-${trainingKey}`}>
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-1.5 min-w-0">
          <Icon className="w-3 h-3 text-slate-400 shrink-0" />
          <span className="text-[10px] uppercase tracking-wider text-slate-400 font-mono truncate">
            {t(`hse_training_${trainingKey}_short`)}
          </span>
        </div>
        <span className={`text-[11px] font-mono font-bold ${color}`}>
          {agg.compliant}/{total || 0}
        </span>
      </div>
      {total === 0 && (
        <p className="text-[9px] text-slate-600 italic mt-0.5">{t('hse_training_na')}</p>
      )}
    </div>
  );
}

function CountChip({ label, value, tone }) {
  const palette = {
    emerald: 'bg-emerald-500/10 text-emerald-300 border-emerald-500/20',
    amber: 'bg-amber-500/10 text-amber-300 border-amber-500/20',
    danger: 'bg-danger/10 text-danger border-danger/20',
  };
  return (
    <div className={`rounded px-2 py-1.5 border text-center ${palette[tone] || palette.emerald}`}>
      <p className="text-base font-display font-bold leading-none">{value}</p>
      <p className="text-[9px] uppercase tracking-wider font-mono mt-0.5">{label}</p>
    </div>
  );
}

function SubScoreCard({ icon: Icon, label, value, caption, weight, testId, weightLabel }) {
  const color =
    value >= 75 ? 'text-emerald-300'
    : value >= 55 ? 'text-sky-300'
    : value >= 35 ? 'text-amber-300'
    : 'text-danger';
  return (
    <div className="bg-ink-700/30 rounded-xl p-3 border border-white/5" data-testid={testId}>
      <div className="flex items-center justify-between mb-1.5">
        <div className="flex items-center gap-1.5">
          <Icon className="w-3.5 h-3.5 text-slate-400" />
          <span className="text-[10px] uppercase tracking-widest text-slate-400 font-mono">{label}</span>
        </div>
        <span className="text-[9px] text-slate-500 font-mono">{weightLabel} {Math.round(weight * 100)}%</span>
      </div>
      <p className={`text-2xl font-display font-bold ${color}`}>{value}<span className="text-sm text-slate-500 font-mono ml-1">/100</span></p>
      <p className="text-[10px] text-slate-500 mt-1">{caption}</p>
    </div>
  );
}
